import React, { useMemo, useState } from "react";
import {
  autoLabelFontSize,
  formatInrLakhs,
} from "./payrollDashboardAnalytics";

/** Solid colors — SVG fill does not reliably paint hsl(var(--ds-*)) */
export const CHART_HEX = [
  "#0f766e",
  "#1d4ed8",
  "#059669",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#ca8a04",
  "#e11d48",
  "#4f46e5",
];

function moneyLabel(v) {
  return formatInrLakhs(v, { forceLakhs: Number(v) >= 1000 });
}

function countLabel(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("en-IN");
}

/** Place value labels above bars without overlapping neighbours. */
function placeBarLabels(nums, max, plotH, topPad, barCenters, barW) {
  const placed = [];
  let lastX = -Infinity;
  let lastY = -Infinity;
  nums.forEach((v, i) => {
    const bh = (v / max) * plotH;
    const lbl = moneyLabel(v);
    const fs = autoLabelFontSize(lbl, Math.max(barW - 2, 10), 6, 10);
    let y = topPad + (plotH - bh) - 4;
    if (y < fs + 2) y = fs + 2;
    // Stagger when too close to previous label
    if (Math.abs(barCenters[i] - lastX) < barW * 0.85 && Math.abs(y - lastY) < fs + 2) {
      y = Math.max(fs + 2, y - (fs + 3));
    }
    placed.push({ x: barCenters[i], y, lbl, fs, show: v !== 0 });
    lastX = barCenters[i];
    lastY = y;
  });
  return placed;
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

export function ChartTypeToggle({ mode, onChange }) {
  const options = [
    { id: "bar", label: "Bar" },
    { id: "pie", label: "Pie" },
    { id: "line", label: "Line" },
    { id: "combo", label: "Combo" },
  ];
  return (
    <div className="inline-flex rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] p-0.5 text-[10px]">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange?.(opt.id)}
          className={`rounded px-2 py-1 font-medium ${
            mode === opt.id
              ? "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-sm"
              : "text-[hsl(var(--ds-muted-fg))]"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function normalizeEntries(data) {
  if (Array.isArray(data)) {
    return data
      .map((d) => ({
        key: d.key || d.label,
        label: d.label || d.key || "",
        value: Number(d.value) || 0,
      }))
      .filter((d) => d.value > 0 || d.value < 0);
  }
  return Object.entries(data || {}).map(([key, value]) => ({
    key,
    label: key,
    value: Number(value) || 0,
  }));
}

/** Donut / pie */
export function DashDonut({ data = [], size = 120, showZero = false }) {
  let segments = normalizeEntries(data);
  if (!showZero) segments = segments.filter((d) => d.value > 0);
  const total = segments.reduce((s, x) => s + Math.abs(Number(x.value) || 0), 0) || 1;
  let angle = -90;
  const r = size / 2;
  const ir = r * 0.55;
  const cx = r;
  const cy = r;

  if (!segments.length) {
    return (
      <div
        className="flex items-center justify-center text-[11px] text-[hsl(var(--ds-muted-fg))]"
        style={{ minHeight: size }}
      >
        No chart data yet
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        {segments.map((seg, i) => {
          const val = Math.abs(Number(seg.value) || 0);
          const sweep = Math.max((val / total) * 360, 0.5);
          const start = angle;
          const end = angle + sweep;
          angle = end;
          const large = sweep > 180 ? 1 : 0;
          const o0 = polarToCartesian(cx, cy, r - 1, start);
          const o1 = polarToCartesian(cx, cy, r - 1, end);
          const i0 = polarToCartesian(cx, cy, ir, end);
          const i1 = polarToCartesian(cx, cy, ir, start);
          const d = `M ${o0.x} ${o0.y} A ${r - 1} ${r - 1} 0 ${large} 1 ${o1.x} ${o1.y} L ${i0.x} ${i0.y} A ${ir} ${ir} 0 ${large} 0 ${i1.x} ${i1.y} Z`;
          return (
            <path key={seg.key || i} d={d} fill={CHART_HEX[i % CHART_HEX.length]}>
              <title>{`${seg.label}: ${moneyLabel(seg.value)}`}</title>
            </path>
          );
        })}
        <text
          x={cx}
          y={cy + 4}
          textAnchor="middle"
          fontSize="11"
          fontWeight="700"
          fill="#0f172a"
        >
          {formatInrLakhs(total)}
        </text>
      </svg>
      <ul className="min-w-[8rem] flex-1 space-y-1.5">
        {segments.map((seg, i) => (
          <li key={seg.key || i} className="flex items-center justify-between gap-2 text-[11px]">
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ background: CHART_HEX[i % CHART_HEX.length] }}
              />
              <span className="truncate text-[hsl(var(--ds-muted-fg))]">{seg.label}</span>
            </span>
            <span className="shrink-0 font-semibold tabular-nums">
              {moneyLabel(seg.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Horizontal bars */
export function DashHBar({ data = [], valueFormat = "inr", maxBars = 10 }) {
  const rows = normalizeEntries(data)
    .map((d) => ({ ...d, value: Math.abs(d.value) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, maxBars);
  const max = Math.max(...rows.map((d) => d.value), 1);
  if (!rows.length) {
    return (
      <div className="py-6 text-center text-[11px] text-[hsl(var(--ds-muted-fg))]">
        No chart data yet
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {rows.map((row, i) => {
        const pct = Math.max(6, (row.value / max) * 100);
        return (
          <div key={row.key || i} className="grid grid-cols-[6.5rem_1fr_4.5rem] items-center gap-2">
            <div className="truncate text-[11px] text-[hsl(var(--ds-muted-fg))]" title={row.label}>
              {row.label}
            </div>
            <div className="h-2.5 overflow-hidden rounded-sm bg-slate-100">
              <div
                className="h-full rounded-sm"
                style={{
                  width: `${pct}%`,
                  background: CHART_HEX[i % CHART_HEX.length],
                }}
              />
            </div>
            <div className="text-right text-[11px] font-semibold tabular-nums">
              {valueFormat === "inr" ? moneyLabel(row.value) : row.value}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Vertical bar chart with Lakhs data labels (auto-placed, non-overlapping) */
export function DashVBar({ labels = [], values = [], height = 150, color = CHART_HEX[0] }) {
  const nums = (values || []).map((v) => Number(v) || 0);
  const max = Math.max(...nums, 1);
  const n = Math.max(nums.length, 1);
  const width = Math.max(260, n * 52);
  if (!nums.length || nums.every((v) => v === 0)) {
    return (
      <div className="py-6 text-center text-[11px] text-[hsl(var(--ds-muted-fg))]">
        No chart data yet
      </div>
    );
  }
  const topPad = 22;
  const axisPad = 22;
  const plotH = height - topPad;
  const barCenters = nums.map((_, i) => i * (width / n) + width / n / 2);
  const barW = Math.max(14, width / n - 16);
  const labelMeta = placeBarLabels(nums, max, plotH, topPad, barCenters, barW);

  return (
    <div className="overflow-x-auto">
      <svg width={width} height={height + axisPad} aria-hidden>
        {nums.map((v, i) => {
          const bh = (v / max) * plotH;
          const x = i * (width / n) + 8;
          const w = Math.max(12, width / n - 16);
          const meta = labelMeta[i];
          return (
            <g key={labels[i] || i}>
              <rect
                x={x}
                y={topPad + plotH - bh}
                width={w}
                height={Math.max(bh, 1)}
                rx="3"
                fill={color}
                opacity={0.85}
              >
                <title>{`${labels[i] || i}: ${meta.lbl}`}</title>
              </rect>
              {meta.show ? (
                <text
                  x={meta.x}
                  y={meta.y}
                  textAnchor="middle"
                  fontSize={meta.fs}
                  fontWeight="600"
                  fill="#0f172a"
                >
                  {meta.lbl}
                </text>
              ) : null}
              <text
                x={x + w / 2}
                y={height + 14}
                textAnchor="middle"
                fontSize={autoLabelFontSize(String(labels[i] || ""), w, 7, 9)}
                fill="#64748b"
              >
                {String(labels[i] || "").slice(5) || labels[i] || ""}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function DashLine({
  values = [],
  labels = [],
  height = 120,
  color = CHART_HEX[0],
  secondValues = null,
  secondColor = CHART_HEX[1],
  valueFormat = "inr",
}) {
  const nums = (values || []).map((v) => Number(v) || 0);
  const nums2 = secondValues ? secondValues.map((v) => Number(v) || 0) : null;
  if (nums.length === 1) {
    // Single point: fall back to bar so labels still show
    const n = nums[0];
    const lbl = valueFormat === "count" ? countLabel(n) : moneyLabel(n);
    return (
      <div className="flex flex-col items-center gap-1 py-4">
        <div className="text-lg font-bold tabular-nums">{lbl}</div>
        <div className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
          {labels[0] || ""}
        </div>
      </div>
    );
  }
  if (nums.length < 2) {
    return (
      <div className="py-6 text-center text-[11px] text-[hsl(var(--ds-muted-fg))]">
        Need 2+ months for a line chart
      </div>
    );
  }
  const all = nums2 ? [...nums, ...nums2] : nums;
  const max = Math.max(...all, 1);
  const min = Math.min(...all, 0);
  const span = Math.max(max - min, 1);
  const width = Math.max(280, nums.length * 56);
  const step = width / (nums.length - 1);
  const topPad = 20;
  const plotH = height - topPad;
  const fmt = (v) => (valueFormat === "count" ? countLabel(v) : moneyLabel(v));
  const toPts = (arr) =>
    arr
      .map((v, i) => {
        const x = i * step;
        const y = topPad + plotH - ((v - min) / span) * (plotH - 8) - 4;
        return `${x},${y}`;
      })
      .join(" ");

  // Alternate label vertical offset to reduce overlap
  const labelYs = nums.map((v, i) => {
    const y = topPad + plotH - ((v - min) / span) * (plotH - 8) - 4;
    const lbl = fmt(v);
    const fs = autoLabelFontSize(lbl, 48, 7, 10);
    const stagger = i % 2 === 0 ? -(fs + 4) : -(fs + 12);
    return { x: i * step, y: Math.max(fs + 2, y + stagger), lbl, fs };
  });

  return (
    <div className="overflow-x-auto">
      <svg width={width} height={height + 24} aria-hidden>
        <polyline fill="none" stroke={color} strokeWidth="2.5" points={toPts(nums)} />
        {nums2 ? (
          <polyline fill="none" stroke={secondColor} strokeWidth="2.5" points={toPts(nums2)} />
        ) : null}
        {nums.map((v, i) => {
          const x = i * step;
          const y = topPad + plotH - ((v - min) / span) * (plotH - 8) - 4;
          const meta = labelYs[i];
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="3.5" fill={color}>
                <title>{`${labels[i]}: ${meta.lbl}`}</title>
              </circle>
              <text
                x={meta.x}
                y={meta.y}
                textAnchor="middle"
                fontSize={meta.fs}
                fontWeight="600"
                fill="#0f172a"
              >
                {meta.lbl}
              </text>
              <text
                x={x}
                y={height + 14}
                textAnchor="middle"
                fontSize={autoLabelFontSize(String(labels[i] || "").slice(5) || labels[i], 40, 7, 9)}
                fill="#64748b"
              >
                {String(labels[i] || "").slice(5) || labels[i] || ""}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function DashCombo({
  labels = [],
  barValues = [],
  lineValues = [],
  barLabel = "Cost",
  lineLabel = "Cash",
  height = 150,
}) {
  const n = Math.max(labels.length, barValues.length, lineValues.length, 1);
  const bars = Array.from({ length: n }, (_, i) => Number(barValues[i]) || 0);
  const lines = Array.from({ length: n }, (_, i) => Number(lineValues[i]) || 0);
  if (!bars.some((v) => v > 0) && !lines.some((v) => v > 0)) {
    return (
      <div className="py-6 text-center text-[11px] text-[hsl(var(--ds-muted-fg))]">
        No chart data yet
      </div>
    );
  }
  const maxB = Math.max(...bars, 1);
  const maxL = Math.max(...lines, 1);
  const width = Math.max(300, n * 56);
  const step = width / n;
  const topPad = 24;
  const plotH = height - topPad;
  const barCenters = bars.map((_, i) => i * step + step / 2);
  const barW = Math.max(12, step - 18);
  const labelMeta = placeBarLabels(bars, maxB, plotH, topPad, barCenters, barW);
  const linePts = lines
    .map((v, i) => {
      const x = i * step + step / 2;
      const y = topPad + plotH - (v / maxL) * (plotH - 10) - 4;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-3 text-[10px] text-[hsl(var(--ds-muted-fg))]">
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHART_HEX[0] }} />
          {barLabel}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-0.5 w-4" style={{ background: CHART_HEX[1] }} />
          {lineLabel}
        </span>
      </div>
      <div className="overflow-x-auto">
        <svg width={width} height={height + 24} aria-hidden>
          {bars.map((v, i) => {
            const bh = (v / maxB) * (plotH - 10);
            const x = i * step + 10;
            const w = Math.max(10, step - 20);
            const meta = labelMeta[i];
            return (
              <g key={`b-${i}`}>
                <rect
                  x={x}
                  y={topPad + plotH - bh}
                  width={w}
                  height={Math.max(bh, 1)}
                  rx="3"
                  fill={CHART_HEX[0]}
                  opacity={0.75}
                >
                  <title>{`${labels[i]} ${barLabel}: ${meta.lbl}`}</title>
                </rect>
                {meta.show ? (
                  <text
                    x={meta.x}
                    y={meta.y}
                    textAnchor="middle"
                    fontSize={meta.fs}
                    fontWeight="600"
                    fill="#0f172a"
                  >
                    {meta.lbl}
                  </text>
                ) : null}
              </g>
            );
          })}
          <polyline fill="none" stroke={CHART_HEX[1]} strokeWidth="2.5" points={linePts} />
          {lines.map((v, i) => {
            const x = i * step + step / 2;
            const y = topPad + plotH - (v / maxL) * (plotH - 10) - 4;
            const lbl = moneyLabel(v);
            const fs = autoLabelFontSize(lbl, 40, 6, 9);
            // Line labels sit slightly right/up to avoid bar labels
            return (
              <g key={`l-${i}`}>
                <circle cx={x} cy={y} r="3" fill={CHART_HEX[1]} />
                <text
                  x={x + 6}
                  y={Math.max(fs + 2, y - 6)}
                  fontSize={fs}
                  fill={CHART_HEX[1]}
                  fontWeight="600"
                >
                  {v > 0 ? lbl : ""}
                </text>
                <title>{`${labels[i]} ${lineLabel}: ${lbl}`}</title>
              </g>
            );
          })}
          {labels.map((l, i) => (
            <text
              key={`t-${l}-${i}`}
              x={i * step + step / 2}
              y={height + 14}
              textAnchor="middle"
              fontSize={autoLabelFontSize(String(l).slice(5) || l, step - 4, 7, 9)}
              fill="#64748b"
            >
              {String(l).slice(5) || l}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
}

export function DashGroupedBars({ labels = [], series = [], height = 140 }) {
  const n = labels.length || 1;
  const all = series.flatMap((s) => s.values || []);
  const max = Math.max(...all.map((v) => Number(v) || 0), 1);
  if (!labels.length || !all.some((v) => Number(v) > 0)) {
    return (
      <div className="py-6 text-center text-[11px] text-[hsl(var(--ds-muted-fg))]">
        No chart data yet
      </div>
    );
  }
  const groupW = 56;
  const width = Math.max(300, n * (groupW + 16));
  const barW = Math.max(6, (groupW - 6) / Math.max(series.length, 1));
  const topPad = 20;
  const plotH = height - topPad;

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-3 text-[10px] text-[hsl(var(--ds-muted-fg))]">
        {series.map((s, i) => (
          <span key={s.name} className="inline-flex items-center gap-1">
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ background: s.color || CHART_HEX[i % CHART_HEX.length] }}
            />
            {s.name}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto">
        <svg width={width} height={height + 24} aria-hidden>
          {labels.map((lab, i) => {
            const gx = i * (groupW + 16) + 10;
            return (
              <g key={lab}>
                {series.map((s, si) => {
                  const v = Number(s.values?.[i]) || 0;
                  const bh = (v / max) * (plotH - 8);
                  const fill = s.color || CHART_HEX[si % CHART_HEX.length];
                  const lbl = countLabel(v);
                  const fs = autoLabelFontSize(lbl, barW + 4, 6, 9);
                  const lx = gx + si * barW + barW / 2;
                  const ly = Math.max(fs + 2, topPad + plotH - bh - 3 - (si % 2) * (fs + 1));
                  return (
                    <g key={`${lab}-${s.name}`}>
                      <rect
                        x={gx + si * barW}
                        y={topPad + plotH - bh}
                        width={barW - 1}
                        height={Math.max(bh, 1)}
                        rx="2"
                        fill={fill}
                      >
                        <title>{`${lab} ${s.name}: ${v}`}</title>
                      </rect>
                      {v > 0 ? (
                        <text
                          x={lx}
                          y={ly}
                          textAnchor="middle"
                          fontSize={fs}
                          fontWeight="600"
                          fill="#0f172a"
                        >
                          {lbl}
                        </text>
                      ) : null}
                    </g>
                  );
                })}
                <text
                  x={gx + groupW / 2}
                  y={height + 14}
                  textAnchor="middle"
                  fontSize="9"
                  fill="#64748b"
                >
                  {String(lab).slice(5) || lab}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

/** Switchable analytics chart for a single metric series or labeled map */
export function AnalyticsChartPanel({
  title,
  data = {},
  labels = [],
  values = [],
  lineValues = null,
  defaultMode = "combo",
}) {
  const [mode, setMode] = useState(defaultMode);
  const pieData = useMemo(() => {
    if (labels.length && values.length) {
      return labels.map((l, i) => ({ label: l, value: Number(values[i]) || 0 }));
    }
    return normalizeEntries(data);
  }, [data, labels, values]);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="ds-small uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
          {title}
        </div>
        <ChartTypeToggle mode={mode} onChange={setMode} />
      </div>
      {mode === "pie" ? (
        <DashDonut data={pieData} />
      ) : mode === "bar" ? (
        labels.length ? (
          <DashVBar labels={labels} values={values} />
        ) : (
          <DashHBar data={pieData} />
        )
      ) : mode === "line" ? (
        <DashLine
          labels={labels}
          values={values}
          secondValues={lineValues}
          secondColor={CHART_HEX[1]}
        />
      ) : (
        <DashCombo
          labels={labels}
          barValues={values}
          lineValues={lineValues || values}
          barLabel="Primary"
          lineLabel={lineValues ? "Secondary" : "Same"}
        />
      )}
    </div>
  );
}
